import { useMemo } from 'react';
import { KeyRound } from 'lucide-react';

import { EnvironmentId } from '@/react/portainer/environments/types';

import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';

import { ScopedKubeconfig, ScopedKubeconfigRow } from '../types';

import { buildColumns } from './columns';
import { TableActions } from './TableActions';

export function ScopedKubeconfigsDatatable({
  dataset,
  isLoading,
  tableKey,
  showOwner,
  showEnvironment,
  environmentNames,
  description,
}: {
  dataset: ScopedKubeconfig[];
  isLoading: boolean;
  tableKey: string;
  showOwner: boolean;
  showEnvironment: boolean;
  environmentNames?: Map<EnvironmentId, string>;
  description?: React.ReactNode;
}) {
  const tableState = useTableState(createPersistedStore(tableKey), tableKey);

  const rows: ScopedKubeconfigRow[] = useMemo(
    () =>
      dataset.map((scopedKubeconfig) => ({
        ...scopedKubeconfig,
        environmentName:
          (scopedKubeconfig.endpointId &&
            environmentNames?.get(scopedKubeconfig.endpointId)) ||
          String(scopedKubeconfig.endpointId ?? ''),
      })),
    [dataset, environmentNames]
  );

  return (
    <Datatable
      columns={buildColumns({ showOwner, showEnvironment })}
      dataset={rows}
      isLoading={isLoading}
      settingsManager={tableState}
      title="Scoped kubeconfigs"
      titleIcon={KeyRound}
      description={description}
      getRowId={(row) => String(row.id)}
      renderTableActions={(selectedItems) => (
        <TableActions selectedItems={selectedItems} />
      )}
      data-cy={`${tableKey}-datatable`}
    />
  );
}
